import React from "react";
import {FaUserCircle} from "react-icons/fa";
import {HiDotsVertical} from "react-icons/hi";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import {
  Box,
  Typography,
  IconButton,
  Skeleton,
  useTheme,
  useMediaQuery,
  Paper,
} from "@mui/material";

function DashboardCard({
  fromColor,
  toColor,
  isLoadingShimmer,
  titleCard,
  dataCard,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Convert Tailwind gradient classes to MUI gradient values
  const getGradientBackground = () => {
    let from, to;

    // Map Tailwind classes to color values
    // This is a simplified mapping and might need adjustments based on your exact colors
    switch (fromColor) {
      case "from-indigo-400":
        from = "#818cf8";
        break;
      case "from-blue-800":
        from = "#1e40af";
        break;
      case "from-emerald-500":
        from = "#10b981";
        break;
      case "from-amber-500":
        from = "#f59e0b";
        break;
      default:
        from = "#818cf8";
    }

    switch (toColor) {
      case "to-cyan-400":
        to = "#22d3ee";
        break;
      case "to-indigo-900":
        to = "#312e81";
        break;
      case "to-emerald-900":
        to = "#064e3b";
        break;
      case "to-pink-500":
        to = "#ec4899";
        break;
      default:
        to = "#22d3ee";
    }

    return `linear-gradient(to right, ${from}, ${to})`;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        width: "100%",
        height: {xs: "120px", sm: "130px", md: "160px"},
        background: getGradientBackground(),
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
        p: {xs: 1.5, sm: 2, md: 2.5},
        boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
      }}
    >
      {/* Background trend icon */}
      <Box
        sx={{
          position: "absolute",
          bottom: {xs: 0, sm: "4px"},
          right: {xs: "-10px", sm: 0},
          transform: {xs: "scale(0.7)", sm: "scale(1)"},
          opacity: 0.1,
        }}
      >
        <TrendingUpIcon
          sx={{
            fontSize: {xs: 90, sm: 100, md: 115},
            color: "white",
          }}
        />
      </Box>

      {/* Content container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          width: "100%",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Top row with title, value and icon */}
        <Box sx={{display: "flex", width: "100%"}}>
          <Box sx={{flexGrow: 1}}>
            {isLoadingShimmer ? (
              <Skeleton
                animation="wave"
                height={isMobile ? 16 : 20}
                width="80%"
                sx={{mb: {xs: 0.5, sm: 0.75}}}
              />
            ) : (
              <Typography
                variant={isMobile ? "subtitle2" : "subtitle1"}
                fontWeight="bold"
                color="white"
                sx={{mb: {xs: 0.5, sm: 0.75}}}
              >
                {titleCard}
              </Typography>
            )}

            {isLoadingShimmer ? (
              <Skeleton
                animation="wave"
                height={isMobile ? 24 : 28}
                width="50%"
                sx={{mb: {xs: 0.5, sm: 0.75}}}
              />
            ) : (
              <Typography
                variant={isMobile ? "h6" : "h5"}
                fontWeight="bold"
                color="white"
                sx={{
                  lineHeight: {xs: 1.2, sm: 1.5},
                }}
              >
                {dataCard}
              </Typography>
            )}
          </Box>

          {/* User icon */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.1)",
                borderRadius: 1.5,
                width: {xs: 28, sm: 32, md: 40},
                height: {xs: 28, sm: 32, md: 40},
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <FaUserCircle
                style={{
                  color: "white",
                  opacity: 0.8,
                  fontSize: isMobile ? 16 : isTablet ? 18 : 24,
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Bottom row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: {xs: 1, sm: 2},
          }}
        >
          {isLoadingShimmer ? (
            <Skeleton animation="wave" height={16} width="60%" />
          ) : (
            <Typography
              variant={isMobile ? "caption" : "body2"}
              fontWeight="medium"
              color="white"
            >
              Last Month
            </Typography>
          )}
          <IconButton
            size={isMobile ? "small" : "medium"}
            sx={{
              p: {xs: 0.25, sm: 1},
              color: "white",
            }}
          >
            <HiDotsVertical
              style={{
                width: isMobile ? 16 : 20,
                height: isMobile ? 16 : 20,
              }}
            />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
}

export default DashboardCard;
